import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import Hero from "../components/Hero";
import FeaturesSection from "../components/FeaturesSection";
import CategorySection from "../components/CategorySection";
import { BookImage } from "../components/BookImage";
import { useCategories, useBooks } from "../hooks";
import { useAuth } from "../contexts/AuthContext";
import { analyticsService } from "../services/analytics/analyticsService";

// Search input component for the book list section
function BookSearchInput() {
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="relative">
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--color-text-muted)]" />
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search for titles"
          className="w-full h-11 pl-12 pr-4 bg-[var(--color-bg-surface)] border border-[var(--color-border-default)] rounded-lg text-[var(--color-text-primary)] placeholder-[var(--color-input-placeholder)] focus:outline-none focus:ring-2 focus:ring-[#FF6900] focus:border-transparent transition-colors"
        />
      </div>
    </form>
  );
}

// Book cards carousel for logged-in users (horizontal scrolling cards)
function BookCardsSection() {
  const { data: booksData, isLoading } = useBooks({
    page: 1,
    limit: 8,
  });

  const books = booksData?.books || [];

  return (
    <section className="py-8" aria-labelledby="continue-reading-heading">
      <div className="max-w-[1280px] mx-auto px-8">
        {/* Section header */}
        <div className="mb-6">
          <h2
            id="continue-reading-heading"
            className="text-2xl font-semibold text-[var(--color-text-primary)]"
          >
            Continue Reading
          </h2>
          <p className="text-sm text-[var(--color-text-secondary)] mt-1">
            Pick up where you left off
          </p>
        </div>

        {/* Horizontal scrolling book cards */}
        <div className="overflow-x-auto scrollbar-hide">
          <div className="flex gap-6">
            {isLoading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    className="flex-none w-[308px] h-[371px] bg-[var(--color-bg-page)] rounded-xl animate-pulse"
                  />
                ))
              : books.slice(0, 4).map(book => (
                  <Link
                    key={book.id}
                    to={`/book/${book.id}`}
                    onClick={() => {
                      analyticsService.track("book.card_clicked", {
                        book_id: book.id,
                        book_title: book.title,
                      });
                    }}
                    className="flex-none w-[308px] group"
                  >
                    <div className="bg-[var(--color-bg-surface)] rounded-xl border border-[var(--color-border-subtle)] overflow-hidden hover:shadow-lg transition-shadow">
                      <BookImage
                        bookId={book.id}
                        title={book.title}
                        className="aspect-[308/250] object-cover"
                        sizes="308px"
                      />
                      <div className="p-4">
                        <h3 className="font-semibold text-[var(--color-text-primary)] line-clamp-1 group-hover:text-[#FF6900] transition-colors">
                          {book.title}
                        </h3>
                        <p className="text-sm text-[var(--color-text-secondary)] mt-1 line-clamp-1">
                          {book.authors.map(a => a.name).join(", ")}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Home() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();

  // Fetch categories from API
  const { data: categoriesData, isLoading: categoriesLoading } = useCategories({
    includeBookCount: true,
  });

  const categories = categoriesData || [];

  return (
    <div className="min-h-screen">
      {/* Hero Section - only for non-logged-in users */}
      {!isAuthenticated && (
        <Hero
          title={t("hero.title", "Bring books to life")}
          subtitle={t(
            "hero.subtitle",
            "Chat with characters, get spoiler-safe summaries, and turn any book into a personal reading companion.",
          )}
          primaryCta={{
            label: t("cta.startChatting", "Get started"),
            to: "/gallery",
          }}
          isLoggedIn={isAuthenticated}
        />
      )}

      {/* Features Section - only for non-logged-in users */}
      {!isAuthenticated && <FeaturesSection />}

      {/* Book Cards Section - only for logged-in users */}
      {isAuthenticated && <BookCardsSection />}

      {/* Book List Section - for all users */}
      <section className="bg-[var(--color-bg-surface)] py-8">
        <div className="max-w-[1280px] mx-auto px-8">
          {/* Search Input */}
          <div className="mb-8">
            <BookSearchInput />
          </div>

          {/* Category Sections */}
          <div className="space-y-8">
            {categoriesLoading
              ? // Loading skeleton for categories
                Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="h-7 bg-[var(--color-border-subtle)] rounded w-48 mb-2" />
                        <div className="h-5 bg-[var(--color-border-subtle)] rounded w-64" />
                      </div>
                      <div className="h-10 bg-[var(--color-border-subtle)] rounded w-24" />
                    </div>
                    <div className="flex gap-4">
                      {Array.from({ length: 8 }).map((_, j) => (
                        <div
                          key={j}
                          className="flex-1 min-w-[120px] aspect-[400/640] bg-[var(--color-border-subtle)] rounded-lg animate-pulse"
                        />
                      ))}
                    </div>
                  </div>
                ))
              : categories
                  .filter(category => (category.bookCount ?? 0) > 0)
                  .map(category => <CategorySection key={category.id} category={category} />)}
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
